import { SmallBasicModal } from '@liveoptics/react-ui';
import React, { useCallback, useState } from 'react';
import { TheaterType, VerifyOwnerState } from '../../../enums';
import { EmailCheckResponse, RegisterNewUserRequest } from '../../../interfaces';
import { OwnerSelectionForm, SetOpportunityForm } from '../../forms';
import SkipVerifyOwnerForm from '../../forms/skip-verify-owner/SkipVerifyOwnerForm';
import { VerifyOwnerSuccessModal } from '../verify-owner-success/VerifyOwnerSuccessModal';

export interface VerifyOwnerModalProps {
  projectId: number;
  defaultTheater: TheaterType;
  isReassign?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const VerifyOwnerModal = (props: VerifyOwnerModalProps) => {
  const [verifyOwnerModalState, setVerifyOwnerModalState] = useState<VerifyOwnerState>(
    VerifyOwnerState.SelectOwner
  );
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalState, setSuccessModalState] = useState<VerifyOwnerState>(
    VerifyOwnerState.SelectOwner
  );
  const [successModalEmail, setSuccessModalEmail] = useState<string>('');
  const [registerNewUserRequest, setRegisterNewUserRequest] = useState<
    RegisterNewUserRequest | undefined
  >();
  const [emailCheckResponse, setEmailCheckResponse] = useState<EmailCheckResponse | undefined>();

  const handleSetOpportunityTransition = useCallback(
    (state: VerifyOwnerState, value?: EmailCheckResponse | RegisterNewUserRequest) => {
      setVerifyOwnerModalState(state);
      if (value && 'ExistingAccountId' in value) setEmailCheckResponse(value);
      else if (value && 'Email' in value) setRegisterNewUserRequest(value);
      else {
        setEmailCheckResponse(undefined);
        setRegisterNewUserRequest(undefined);
      }
    },
    []
  );

  const handleSuccess = useCallback(
    (state: VerifyOwnerState, email?: string) => {
      setShowSuccessModal(true);
      setSuccessModalState(state);
      if (email) setSuccessModalEmail(email);
      props.onSuccess();
    },
    [props]
  );

  const getModalTitle = (verifyOwnerModalState: VerifyOwnerState, isReassign?: boolean): string => {
    if (verifyOwnerModalState === VerifyOwnerState.SetOpportunity) return 'Link Opportunity ID';
    if (isReassign) return 'Reassign Owner';
    return 'Verify Owner';
  };

  const getForm = (state: VerifyOwnerState) => {
    switch (state) {
      case VerifyOwnerState.SelectOwner:
        return (
          <OwnerSelectionForm
            projectId={props.projectId}
            defaultTheater={props.defaultTheater}
            emailCheckResponse={emailCheckResponse}
            registerNewUserRequest={registerNewUserRequest}
            verifyOwnerStateCallback={handleSetOpportunityTransition}
            onCancel={props.onClose}
            onSuccess={handleSuccess}
          />
        );
      case VerifyOwnerState.SkipVerifyOwner:
        return <SkipVerifyOwnerForm onCancel={props.onClose} />;
      case VerifyOwnerState.SetOpportunity:
        return (
          <SetOpportunityForm
            projectId={props.projectId}
            defaultTheater={props.defaultTheater}
            verifyOwnerStateCallback={setVerifyOwnerModalState}
            emailCheckResponse={emailCheckResponse}
            registerNewUserRequest={registerNewUserRequest}
            onCancel={props.onClose}
            onSuccess={handleSuccess}
          />
        );
    }
  };

  return showSuccessModal ? (
    <VerifyOwnerSuccessModal
      isReassign={props.isReassign}
      state={successModalState}
      email={successModalEmail}
      onClose={props.onClose}
    />
  ) : (
    <SmallBasicModal
      modalTitle={getModalTitle(verifyOwnerModalState, props.isReassign)}
      onClose={props.onClose}
    >
      {getForm(verifyOwnerModalState)}
    </SmallBasicModal>
  );
};
